import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Button from '../components/Button';
import { useAuth } from '../context/AuthContext';
import { proxiedImageUrl } from '../lib/imageProxy';
import './PlayingCard.css';

const formatHeight = (inches) => {
  if (!inches) return '—';
  const total = Math.round(inches); // avoid float-modulo artifacts on fractional heights
  return `${Math.floor(total / 12)}'${total % 12}"`;
};

function ModernChrome({ a, apiUrl }) {
  const positions = [a.position_primary, a.position_secondary, a.position_other].filter(Boolean).join(' / ');
  return (
    <div className="card-container card-modern">
      {a.primary_photo ? (
        <img src={proxiedImageUrl(a.primary_photo, apiUrl)} alt={a.name} className="card-modern__photo" />
      ) : (
        <div className="card-modern__photo-placeholder">&#9899;</div>
      )}
      <div className="card-modern__info">
        <div className="card-modern__name">{a.name}</div>
        <div className="card-modern__school">
          {a.school_logo && <img src={proxiedImageUrl(a.school_logo, apiUrl)} alt="" className="card-modern__school-logo" />}
          <span>{(a.school ?? a.high_school) || ''}</span>
        </div>
        <div className="card-modern__meta">
          {a.graduation_year && <span>Class of {a.graduation_year}</span>}
          {positions && <span>{positions}</span>}
          <span>{formatHeight(a.height_inches)} / {a.weight_lbs || '—'} lbs</span>
        </div>
      </div>
      <div className="card-modern__stats">
        <div className="card-modern__stat">
          <div className="card-modern__stat-value">{a.dash_40 || '—'}</div>
          <div className="card-modern__stat-label">40 Yard</div>
        </div>
        <div className="card-modern__stat">
          <div className="card-modern__stat-value">{a.vertical_jump ? `${a.vertical_jump}"` : '—'}</div>
          <div className="card-modern__stat-label">Vertical</div>
        </div>
        <div className="card-modern__stat">
          <div className="card-modern__stat-value">{a.bench_press || '—'}</div>
          <div className="card-modern__stat-label">Bench</div>
        </div>
        <div className="card-modern__stat">
          <div className="card-modern__stat-value">{a.gpa_weighted || '—'}</div>
          <div className="card-modern__stat-label">GPA</div>
        </div>
        <div className="card-modern__stat">
          <div className="card-modern__stat-value">{a.squat || '—'}</div>
          <div className="card-modern__stat-label">Squat</div>
        </div>
        <div className="card-modern__stat">
          <div className="card-modern__stat-value">{formatHeight(a.broad_jump)}</div>
          <div className="card-modern__stat-label">Broad Jump</div>
        </div>
      </div>
    </div>
  );
}

function Classic80s({ a, apiUrl }) {
  const positions = [a.position_primary, a.position_secondary, a.position_other].filter(Boolean).join(' / ');
  return (
    <div className="card-container card-classic">
      <div className="card-classic__header">
        <span className="card-classic__sport-label">{a.sport || 'Football'}</span>
        <span className="card-classic__year">{a.graduation_year ? `Class of ${a.graduation_year}` : ''}</span>
      </div>
      <div className="card-classic__photo-frame">
        <div className="card-classic__corner card-classic__corner--tl" />
        <div className="card-classic__corner card-classic__corner--tr" />
        <div className="card-classic__corner card-classic__corner--bl" />
        <div className="card-classic__corner card-classic__corner--br" />
        {a.primary_photo ? (
          <img src={proxiedImageUrl(a.primary_photo, apiUrl)} alt={a.name} className="card-classic__photo" />
        ) : (
          <div className="card-classic__photo-placeholder">&#9899;</div>
        )}
      </div>
      <div className="card-classic__name-bar">
        <div className="card-classic__name">{a.name}</div>
        {positions && <div className="card-classic__position">{positions}</div>}
      </div>
      <div className="card-classic__school-row">
        {a.school_logo && <img src={proxiedImageUrl(a.school_logo, apiUrl)} alt="" className="card-classic__school-logo" />}
        <span>{(a.school ?? a.high_school) || ''}</span>
      </div>
      <div className="card-classic__stats-grid">
        <div className="card-classic__stat-cell">
          <div className="card-classic__stat-value">{formatHeight(a.height_inches)}</div>
          <div className="card-classic__stat-label">Height</div>
        </div>
        <div className="card-classic__stat-cell">
          <div className="card-classic__stat-value">{a.weight_lbs || '—'}</div>
          <div className="card-classic__stat-label">Weight</div>
        </div>
        <div className="card-classic__stat-cell">
          <div className="card-classic__stat-value">{a.dash_40 || '—'}</div>
          <div className="card-classic__stat-label">40 Yard</div>
        </div>
        <div className="card-classic__stat-cell">
          <div className="card-classic__stat-value">{a.vertical_jump ? `${a.vertical_jump}"` : '—'}</div>
          <div className="card-classic__stat-label">Vertical</div>
        </div>
        <div className="card-classic__stat-cell">
          <div className="card-classic__stat-value">{a.bench_press || '—'}</div>
          <div className="card-classic__stat-label">Bench</div>
        </div>
        <div className="card-classic__stat-cell">
          <div className="card-classic__stat-value">{a.gpa_weighted || '—'}</div>
          <div className="card-classic__stat-label">GPA</div>
        </div>
      </div>
    </div>
  );
}

function BowmanDraft({ a, apiUrl }) {
  const positions = [a.position_primary, a.position_secondary, a.position_other].filter(Boolean).join(' / ');
  return (
    <div className="card-container card-bowman">
      <div className="card-bowman__draft-label">Draft Prospect</div>
      {a.primary_photo ? (
        <img src={proxiedImageUrl(a.primary_photo, apiUrl)} alt={a.name} className="card-bowman__photo" />
      ) : (
        <div className="card-bowman__photo-placeholder">&#9899;</div>
      )}
      <div className="card-bowman__info">
        <div className="card-bowman__name">{a.name}</div>
        <div className="card-bowman__school">
          {a.school_logo && <img src={proxiedImageUrl(a.school_logo, apiUrl)} alt="" className="card-bowman__school-logo" />}
          <span>{(a.school ?? a.high_school) || ''}</span>
        </div>
        <div className="card-bowman__meta">
          {a.graduation_year && <span>Class of {a.graduation_year}</span>}
          {positions && <span>{positions}</span>}
        </div>
      </div>
      <div className="card-bowman__gold-line" />
      <div className="card-bowman__stats">
        <div className="card-bowman__stat">
          <div className="card-bowman__stat-value">{formatHeight(a.height_inches)}</div>
          <div className="card-bowman__stat-label">Height</div>
        </div>
        <div className="card-bowman__stat">
          <div className="card-bowman__stat-value">{a.weight_lbs || '—'}</div>
          <div className="card-bowman__stat-label">Weight</div>
        </div>
        <div className="card-bowman__stat">
          <div className="card-bowman__stat-value">{a.dash_40 || '—'}</div>
          <div className="card-bowman__stat-label">40 Yard</div>
        </div>
        <div className="card-bowman__stat">
          <div className="card-bowman__stat-value">{a.vertical_jump ? `${a.vertical_jump}"` : '—'}</div>
          <div className="card-bowman__stat-label">Vertical</div>
        </div>
        <div className="card-bowman__stat">
          <div className="card-bowman__stat-value">{a.bench_press || '—'}</div>
          <div className="card-bowman__stat-label">Bench</div>
        </div>
        <div className="card-bowman__stat">
          <div className="card-bowman__stat-value">{a.gpa_weighted || '—'}</div>
          <div className="card-bowman__stat-label">GPA</div>
        </div>
      </div>
    </div>
  );
}

export default function PlayingCardPage() {
  const { API_URL } = useAuth();
  const params = useParams();
  // Support both /athlete/:uid/:profileUrl and legacy /athlete/:profileUrl
  const profileUrl = params.profileUrl || params.uid;
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [style, setStyle] = useState('modern');
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);
  const isInIframe = window.self !== window.top;

  useEffect(() => {
    document.title = 'Player Card | ProZpects';
    const apiBase = window.location.origin;
    fetch(`${apiBase}/api/athletes/public/${profileUrl}`)
      .then(res => {
        if (!res.ok) throw new Error('Profile not found');
        return res.json();
      })
      .then(data => {
        setAthlete(data);
        document.title = `${data.name} Card | ProZpects`;
      })
      .catch(() => setError('Profile not found or not available.'))
      .finally(() => setLoading(false));
  }, [profileUrl]);

  const handleDownload = async () => {
    if (!cardRef.current || downloading) return;
    setDownloading(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
        width: 600,
        height: 900,
      });
      const link = document.createElement('a');
      const safeName = (athlete.name || 'player').replace(/\s+/g, '_');
      link.download = `${safeName}_card.jpg`;
      link.href = canvas.toDataURL('image/jpeg', 0.95);
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    const uid = athlete?.uid || '0';
    const pUrl = athlete?.profile_url || profileUrl;
    const url = `${window.location.origin}/athlete/${uid}/${pUrl}/card`;
    if (navigator.share) {
      try { await navigator.share({ title: `${athlete?.name || 'Player'} Card | ProZpects`, url }); } catch (e) { /* cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) { /* fallback */ }
    }
  };

  if (loading) return (
    <div className="playing-card-page">
      <div className="loading-spinner" style={{ color: '#94a3b8' }}>Loading...</div>
    </div>
  );

  if (error) return (
    <div className="playing-card-page">
      <div style={{ color: '#ef4444', textAlign: 'center', marginTop: 40 }}>
        <h2 style={{ color: '#fff', marginBottom: 8 }}>Profile Not Found</h2>
        <p>{error}</p>
      </div>
    </div>
  );

  return (
    <div className="playing-card-page">
      <div className="playing-card-wrapper">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px', alignSelf: 'stretch' }}>
          <button
            onClick={handleShare}
            title="Share card"
            aria-label="Share card"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '6px 14px', fontSize: '13px', fontWeight: 500,
              color: '#cbd5e1', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '8px', cursor: 'pointer'
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
            {copied ? 'Copied!' : 'Share'}
          </button>
        </div>

        <div ref={cardRef}>
          {style === 'modern' && <ModernChrome a={athlete} apiUrl={API_URL} />}
          {style === 'classic' && <Classic80s a={athlete} apiUrl={API_URL} />}
          {style === 'bowman' && <BowmanDraft a={athlete} apiUrl={API_URL} />}
        </div>

        <div className="playing-card-tabs" style={{ marginTop: '16px' }}>
          <button
            className={`playing-card-tab ${style === 'modern' ? 'playing-card-tab--active' : ''}`}
            onClick={() => setStyle('modern')}
          >
            Modern Chrome
          </button>
          <button
            className={`playing-card-tab ${style === 'classic' ? 'playing-card-tab--active' : ''}`}
            onClick={() => setStyle('classic')}
          >
            Classic 80s/90s
          </button>
          <button
            className={`playing-card-tab ${style === 'bowman' ? 'playing-card-tab--active' : ''}`}
            onClick={() => setStyle('bowman')}
          >
            Bowman Draft
          </button>
        </div>

        <Button
          variant="primary"
          className="playing-card-download"
          onClick={handleDownload}
          disabled={downloading}
        >
          {downloading ? 'Generating...' : 'Download JPG'}
        </Button>
      </div>
    </div>
  );
}
